import { differenceInDays, format, eachDayOfInterval, parseISO, addDays } from 'date-fns';
import { toJsDate } from './dateUtils';

export const expandCustodySchedule = (schedule, startDate, endDate) => {
  if (!schedule || !schedule.cycleDays || !schedule.startDate) return {};

  const scheduleStart = toJsDate(schedule.startDate);
  const scheduleEnd = schedule.endDate ? toJsDate(schedule.endDate) : null;

  let effectiveStart = startDate;
  let effectiveEnd = endDate;
  if (scheduleStart > effectiveStart) effectiveStart = scheduleStart;
  if (scheduleEnd && scheduleEnd < effectiveEnd) effectiveEnd = scheduleEnd;
  if (effectiveStart > effectiveEnd) return {};

  const days = eachDayOfInterval({ start: effectiveStart, end: effectiveEnd });
  const result = {};

  days.forEach(day => {
    const diff = differenceInDays(day, scheduleStart);
    const cycleIndex = ((diff % schedule.cycleLength) + schedule.cycleLength) % schedule.cycleLength;
    const parentIndex = schedule.cycleDays[cycleIndex];
    result[format(day, 'yyyy-MM-dd')] = parentIndex;
  });

  return result;
};

// Returns the inclusive list of 'yyyy-MM-dd' strings an override covers.
// A single-day swap covers [date]. A trip covers every day from startDate..endDate.
export const getOverrideDateStrings = (override) => {
  const toStr = (v) => (typeof v === 'string' ? v : format(toJsDate(v), 'yyyy-MM-dd'));

  if (override.type === 'trip' && override.startDate && override.endDate) {
    const start = parseISO(toStr(override.startDate));
    const end = parseISO(toStr(override.endDate));
    if (end < start) return []; // guard bad ranges — paint nothing rather than crash
    return eachDayOfInterval({ start, end }).map((d) => format(d, 'yyyy-MM-dd'));
  }

  return [toStr(override.date)];
};

export const applyOverrides = (expandedSchedule, overrides, parentIds) => {
  const result = { ...expandedSchedule };

  // Process trips first, single-day swaps last, so a granular per-day swap
  // always wins over a bulk trip on any day they both cover.
  const ordered = [...overrides].sort(
    (a, b) => (a.type === 'trip' ? 0 : 1) - (b.type === 'trip' ? 0 : 1)
  );

  ordered.forEach(override => {
    if (override.status !== 'confirmed' && override.status !== 'proposed') return;

    getOverrideDateStrings(override).forEach(dateStr => {
      if (result[dateStr] === undefined) return;

      let newParentIndex = parentIds.indexOf(override.newParentId) + 1;

      if (newParentIndex === 0 && parentIds.length === 2) {
        const origIndex = parentIds.indexOf(override.originalParentId);
        if (origIndex !== -1) {
          newParentIndex = origIndex === 0 ? 2 : 1;
        }
      }

      if (newParentIndex > 0) {
        const currentIndex = typeof result[dateStr] === 'number'
          ? result[dateStr]
          : result[dateStr].parentIndex;
        result[dateStr] = {
          parentIndex: override.status === 'confirmed' ? newParentIndex : currentIndex,
          isOverride: true,
          status: override.status,
          overrideId: override.id,
          reason: override.reason,
          isTrip: override.type === 'trip',
          tripTitle: override.type === 'trip' ? (override.title || null) : null
        };
      }
    });
  });

  return result;
};

export const getParentForDate = (expandedSchedule, dateStr) => {
  const entry = expandedSchedule[dateStr];
  if (!entry) return null;

  if (typeof entry === 'number') {
    return { parentIndex: entry, isOverride: false };
  }
  return entry;
};

export const buildFullCustodyMap = (schedules, overrides, family, startDate, endDate) => {
  let expanded = {};

  if (schedules.length) {
    schedules.forEach(schedule => {
      const scheduleExpanded = expandCustodySchedule(schedule, startDate, endDate);
      expanded = { ...expanded, ...scheduleExpanded };
    });
    expanded = applyOverrides(expanded, overrides, family.parentIds);
  }

  return expanded;
};

export const custodyToCalendarEvents = (expandedSchedule, parentProfiles, parentIds) => {
  const events = [];
  const entries = Object.entries(expandedSchedule);

  entries.forEach(([dateStr, entry]) => {
    const parentIndex = typeof entry === 'number' ? entry : entry.parentIndex;
    const parentId = parentIds[parentIndex - 1];
    const profile = parentProfiles[parentId];
    const isOverride = typeof entry === 'object' && entry.isOverride;
    const isPending = typeof entry === 'object' && entry.status === 'proposed';
    const isTrip = typeof entry === 'object' && !!entry.isTrip;
    const tripTitle = typeof entry === 'object' ? (entry.tripTitle || null) : null;

    const date = parseISO(dateStr);
    events.push({
      id: `custody-${dateStr}`,
      title: `${profile?.displayName || 'Parent ' + parentIndex}'s day`,
      start: date,
      end: date,
      allDay: true,
      resource: {
        type: 'custody',
        parentIndex,
        parentId,
        color: profile?.color || (parentIndex === 1 ? '#3b82f6' : '#ec4899'),
        isOverride,
        isPending,
        isTrip,
        tripTitle
      }
    });
  });

  return events;
};
